import { useState } from "react";
import SiteHeader from "./SiteHeader";
import { sendContactEmail } from "../integrations/emailClient";
import { trackEvent } from "../utils/analytics";
import "../styles/components/ContactPage.css";
import futureForwardImg from "../assets/future-forward.png";

const faqs = [
  {
    question: "Q. Will the sessions be recorded?",
    answer:
      "Yes, all keynotes and main sessions will be recorded and available to registered attendees for 90 days after the conference.",
  },
  {
    question: "Q. Is there a dress code?",
    answer:
      "Business casual attire is recommended for conference days. The Awards Gala Dinner on Tuesday evening is a formal event.",
  },
  {
    question: "Q. Are meals included?",
    answer:
      "Yes, your registration includes all breakfasts, lunches, coffee breaks, and the Awards Gala Dinner. Dietary restrictions will be accommodated.",
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await sendContactEmail({
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        message: formData.message.trim(),
      });

      if (result.success || result.skipped) {
        trackEvent("form_submit", {
          form_name: "contact_us",
          form_destination: "/contact",
        });
        setSubmitStatus({
          type: "success",
          title: "Message Sent!",
          message: "Thank you for reaching out. We'll get back to you within 24 hours.",
        });
        setFormData({ name: "", email: "", company: "", message: "" });
      } else {
        setSubmitStatus({
          type: "error",
          title: "Unable to Send",
          message: "Something went wrong. Please try again or email us directly.",
        });
      }
    } catch {
      setSubmitStatus({
        type: "error",
        title: "Unable to Send",
        message: "An error occurred. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact">
      <SiteHeader />

      <main className="contact__main">
        <section className="contact__hero">
          <div className="container contact__heroInner">
            <div className="contact__heroKicker">
              2026 MOËT HENNESSY
              <br />
              DISTRIBUTOR CONFERENCE
            </div>
            <h1 className="contact__heroTitle">Contact Us</h1>
            <img
              src={futureForwardImg}
              alt="Future Forward"
              className="contact__heroImage"
            />
          </div>
        </section>

        <section className="contact__intro">
          <div className="container contact__introInner">
            <p className="contact__introText">
              Have questions about the conference? We&apos;re here to help.
            </p>
          </div>
        </section>

        <section className="contact__formSection">
          <div className="container contact__formInner">
            <div className="contact__formCard">
              <div className="contact__formHeading">Send Us a Message</div>
              <form className="contact__form" onSubmit={handleSubmit} noValidate>
                <label className="contact__label" htmlFor="contact-name">
                  Name*
                </label>
                <input
                  className={`contact__input ${errors.name ? "contact__input--error" : ""}`}
                  id="contact-name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleInputChange}
                />
                {errors.name && <span className="contact__error">{errors.name}</span>}

                <label className="contact__label" htmlFor="contact-email">
                  Email Address*
                </label>
                <input
                  className={`contact__input ${errors.email ? "contact__input--error" : ""}`}
                  id="contact-email"
                  name="email"
                  type="email"
                  placeholder="john.doe@company.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {errors.email && <span className="contact__error">{errors.email}</span>}

                <label className="contact__label" htmlFor="contact-company">
                  Company Name*
                </label>
                <input
                  className={`contact__input ${errors.company ? "contact__input--error" : ""}`}
                  id="contact-company"
                  name="company"
                  type="text"
                  placeholder="Good Company"
                  value={formData.company}
                  onChange={handleInputChange}
                />
                {errors.company && <span className="contact__error">{errors.company}</span>}

                <label className="contact__label" htmlFor="contact-message">
                  Message*
                </label>
                <textarea
                  className={`contact__textarea ${errors.message ? "contact__textarea--error" : ""}`}
                  id="contact-message"
                  name="message"
                  placeholder="Tell us how we can help."
                  value={formData.message}
                  onChange={handleInputChange}
                />
                {errors.message && <span className="contact__error">{errors.message}</span>}

                <p className="contact__formNote">
                  We typically respond within 24 hours during business days.
                </p>

                <button
                  className="contact__submit"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="contact__faqSection">
          <div className="container contact__faqInner">
            <h2 className="contact__sectionTitle">Frequently Asked Questions</h2>
            <div className="contact__faqCard">
              {faqs.map((faq) => (
                <div className="contact__faqItem" key={faq.question}>
                  <div className="contact__faqQuestion">{faq.question}</div>
                  <div className="contact__faqAnswer">{faq.answer}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {submitStatus && (
        <div className="contact__modalOverlay" onClick={() => setSubmitStatus(null)}>
          <div
            className={`contact__modal contact__modal--${submitStatus.type}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contact__modalIcon">
              {submitStatus.type === "success" ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12l2.5 2.5L16 9" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
              )}
            </div>
            <h3 className="contact__modalTitle">{submitStatus.title}</h3>
            <p className="contact__modalMessage">{submitStatus.message}</p>
            <button
              className="contact__modalButton"
              type="button"
              onClick={() => setSubmitStatus(null)}
            >
              {submitStatus.type === "success" ? "Done" : "Try Again"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
